import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Card,
  Badge,
  Alert,
  Spinner,
} from 'react-bootstrap';
import RichTextEditor from '../components/article/RichTextEditor';
import {
  Article,
  ArticleFormData,
  ArticleCategory,
  ArticleTag,
} from '../models/article';
import articleService from '../services/articleService';
import userService from '../services/userService';
import AdminMenuButton from '../components/common/AdminMenuButton';
import SideMenu from '../components/common/SideMenu';
import { useAuth } from '../hooks/useAuth';
import { User } from '../models/user';

const ArticleEditor: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEdit = !!id;
  const { authData } = useAuth();

  // Form state
  const [formData, setFormData] = useState<ArticleFormData>({
    title: '',
    content: '',
    summary: '',
    categories: [],
    tags: [],
    featuredImage: null,
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Data state
  const [categories, setCategories] = useState<ArticleCategory[]>([]);
  const [tags, setTags] = useState<ArticleTag[]>([]);
  const [existingArticle, setExistingArticle] = useState<Article | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  // New category/tag state
  const [newCategory, setNewCategory] = useState('');
  const [newTag, setNewTag] = useState('');

  // 初期データの読み込み
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // カテゴリとタグを並行して取得
        const [categoriesData, tagsData] = await Promise.all([
          articleService.getCategories(),
          articleService.getTags(),
        ]);

        setCategories(categoriesData);
        setTags(tagsData);

        // 編集モードの場合は記事データも取得
        if (isEdit && id) {
          const articleData = await articleService.getArticle(parseInt(id));
          setExistingArticle(articleData);
          setFormData({
            title: articleData.title,
            content: articleData.content,
            summary: articleData.summary || '',
            categories: articleData.categories,
            tags: articleData.tags,
            featuredImage: articleData.featuredImage || null,
          });
        }
      } catch (err) {
        setError('データの読み込みに失敗しました');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [isEdit, id]);

  // 現在のユーザー情報を取得
  useEffect(() => {
    const loadCurrentUser = async () => {
      if (authData.userId) {
        try {
          const userData = await userService.getUser(authData.userId);
          setCurrentUser(userData);
        } catch (err) {
          console.error('ユーザー情報の取得に失敗しました:', err);
        }
      }
    };

    loadCurrentUser();
  }, [authData.userId]);

  // フォーム送信
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      setError('タイトルを入力してください');
      return;
    }

    if (!formData.content.trim()) {
      setError('記事の内容を入力してください');
      return;
    }

    if (!currentUser) {
      setError('ユーザー情報が取得できませんでした');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // 作成者情報を含むフォームデータを準備
      const submissionData = {
        ...formData,
        authorId: currentUser.id,
        authorName: currentUser.username,
      };

      if (isEdit && id) {
        await articleService.updateArticle(parseInt(id), submissionData);
        setSuccess('記事を更新しました');
      } else {
        await articleService.createArticle(submissionData);
        setSuccess('記事を作成しました');
      }

      // 成功後、記事一覧に戻る
      setTimeout(() => {
        navigate('/admin/articles');
      }, 2000);
    } catch (err) {
      setError(
        isEdit ? '記事の更新に失敗しました' : '記事の作成に失敗しました'
      );
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  // カテゴリの追加
  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;

    try {
      const category = await articleService.createCategory({
        name: newCategory,
        slug: newCategory.toLowerCase().replace(/\s+/g, '-'),
        description: '',
        color: '#007bff',
      });

      setCategories([...categories, category]);
      setNewCategory('');
    } catch (err) {
      setError('カテゴリの追加に失敗しました');
    }
  };

  // タグの追加
  const handleAddTag = async () => {
    if (!newTag.trim()) return;

    try {
      const tag = await articleService.createTag({
        name: newTag,
        slug: newTag.toLowerCase().replace(/\s+/g, '-'),
      });

      setTags([...tags, tag]);
      setNewTag('');
    } catch (err) {
      setError('タグの追加に失敗しました');
    }
  };

  // カテゴリの選択/解除
  const handleCategoryChange = (categoryName: string) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.includes(categoryName)
        ? prev.categories.filter(c => c !== categoryName)
        : [...prev.categories, categoryName],
    }));
  };

  // タグの選択/解除
  const handleTagChange = (tagName: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tagName)
        ? prev.tags.filter(t => t !== tagName)
        : [...prev.tags, tagName],
    }));
  };

  // アイキャッチ画像の変更
  const handleFeaturedImageChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0] || null;
    setFormData(prev => ({ ...prev, featuredImage: file }));
  };

  if (loading) {
    return (
      <Container fluid>
        <Row className="justify-content-center py-5">
          <Col xs={12} className="text-center">
            <Spinner animation="border" role="status" className="text-success">
              <span className="visually-hidden">読み込み中...</span>
            </Spinner>
            <div className="mt-3">データを読み込み中...</div>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Row>
        <Col xs={12} md={2}>
          <SideMenu />
        </Col>

        <Col xs={12} md={10}>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2 className="text-warning white-stroke">
              {isEdit ? '記事編集' : '新規記事作成'}
            </h2>
            <AdminMenuButton onToggle={() => {}} isOpen={false} />
          </div>

          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}

          <Form onSubmit={handleSubmit}>
            <Row>
              <Col lg={8}>
                {/* タイトル */}
                <Card className="mb-4">
                  <Card.Header>
                    <h5 className="mb-0">記事タイトル</h5>
                  </Card.Header>
                  <Card.Body>
                    <Form.Group>
                      <Form.Control
                        type="text"
                        placeholder="記事のタイトルを入力してください"
                        value={formData.title}
                        onChange={e =>
                          setFormData(prev => ({
                            ...prev,
                            title: e.target.value,
                          }))
                        }
                        required
                      />
                    </Form.Group>
                  </Card.Body>
                </Card>

                {/* 記事内容 */}
                <Card className="mb-4">
                  <Card.Header>
                    <h5 className="mb-0">記事内容</h5>
                  </Card.Header>
                  <Card.Body>
                    <RichTextEditor
                      value={formData.content}
                      onChange={content =>
                        setFormData(prev => ({ ...prev, content }))
                      }
                      height={600}
                    />
                  </Card.Body>
                </Card>

                {/* 要約 */}
                <Card className="mb-4">
                  <Card.Header>
                    <h5 className="mb-0">記事の要約</h5>
                  </Card.Header>
                  <Card.Body>
                    <Form.Group>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="記事の要約を入力してください（一覧表示で使用されます）"
                        value={formData.summary}
                        onChange={e =>
                          setFormData(prev => ({
                            ...prev,
                            summary: e.target.value,
                          }))
                        }
                      />
                    </Form.Group>
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={4}>
                {/* アクション */}
                <Card className="mb-4">
                  <Card.Header>
                    <h5 className="mb-0">アクション</h5>
                  </Card.Header>
                  <Card.Body>
                    <div className="d-grid gap-2">
                      <Button variant="primary" type="submit" disabled={saving}>
                        {saving ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            {isEdit ? '更新中...' : '作成中...'}
                          </>
                        ) : isEdit ? (
                          '記事を更新'
                        ) : (
                          '記事を作成'
                        )}
                      </Button>
                      <Button
                        variant="outline-secondary"
                        onClick={() => navigate('/admin/articles')}
                      >
                        キャンセル
                      </Button>
                    </div>
                  </Card.Body>
                </Card>

                {/* アイキャッチ画像 */}
                <Card className="mb-4">
                  <Card.Header>
                    <h5 className="mb-0">アイキャッチ画像</h5>
                  </Card.Header>
                  <Card.Body>
                    <Form.Group>
                      <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={handleFeaturedImageChange}
                      />
                      {existingArticle?.featuredImage &&
                        !formData.featuredImage && (
                          <div className="mt-2">
                            <img
                              src={existingArticle.featuredImage}
                              alt="現在のアイキャッチ画像"
                              style={{
                                maxWidth: '100%',
                                maxHeight: '200px',
                                objectFit: 'cover',
                              }}
                              className="img-thumbnail"
                            />
                          </div>
                        )}
                    </Form.Group>
                  </Card.Body>
                </Card>

                {/* カテゴリ */}
                <Card className="mb-4">
                  <Card.Header>
                    <h5 className="mb-0">カテゴリ</h5>
                  </Card.Header>
                  <Card.Body>
                    <div className="mb-3">
                      {categories.map(category => (
                        <Form.Check
                          key={category.id}
                          type="checkbox"
                          id={`category-${category.id}`}
                          label={category.name}
                          checked={formData.categories.includes(category.name)}
                          onChange={() => handleCategoryChange(category.name)}
                          className="mb-2"
                        />
                      ))}
                    </div>

                    <div className="border-top pt-3">
                      <Form.Group className="mb-2">
                        <Form.Control
                          type="text"
                          placeholder="新しいカテゴリ名"
                          value={newCategory}
                          onChange={e => setNewCategory(e.target.value)}
                        />
                      </Form.Group>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={handleAddCategory}
                        disabled={!newCategory.trim()}
                      >
                        カテゴリを追加
                      </Button>
                    </div>

                    <div className="mt-3">
                      <strong>選択中:</strong>
                      <div className="mt-1">
                        {formData.categories.map(category => (
                          <Badge
                            key={category}
                            bg="primary"
                            className="me-1 mb-1"
                          >
                            {category}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </Card.Body>
                </Card>

                {/* タグ */}
                <Card className="mb-4">
                  <Card.Header>
                    <h5 className="mb-0">タグ</h5>
                  </Card.Header>
                  <Card.Body>
                    <div className="mb-3">
                      {tags.map(tag => (
                        <Form.Check
                          key={tag.id}
                          type="checkbox"
                          id={`tag-${tag.id}`}
                          label={tag.name}
                          checked={formData.tags.includes(tag.name)}
                          onChange={() => handleTagChange(tag.name)}
                          className="mb-2"
                        />
                      ))}
                    </div>

                    <div className="border-top pt-3">
                      <Form.Group className="mb-2">
                        <Form.Control
                          type="text"
                          placeholder="新しいタグ名"
                          value={newTag}
                          onChange={e => setNewTag(e.target.value)}
                        />
                      </Form.Group>
                      <Button
                        variant="outline-success"
                        size="sm"
                        onClick={handleAddTag}
                        disabled={!newTag.trim()}
                      >
                        タグを追加
                      </Button>
                    </div>

                    <div className="mt-3">
                      <strong>選択中:</strong>
                      <div className="mt-1">
                        {formData.tags.map(tag => (
                          <Badge key={tag} bg="success" className="me-1 mb-1">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
    </Container>
  );
};

export default ArticleEditor;
